import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileCopy as FileCopyIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import { formatDate } from '../../../common/formatter';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import {
  useGetUserPendingDocuments,
  useGetUserPendingDocumentStats
} from '../../../user/User.hooks';
import { FileDto } from '../../documents/Document.interface';
import { Task } from '../../tasks/Task.interface';
import { DashboardCardProps, DashboardItem } from '../Dashboard.interface';
import BaseDashboardCard, { DashboardCardOrder } from './BaseDashboardCard';
import { parseTaskStatus } from '../../tasks/task.helper';

const orderMap: { [key in DashboardCardOrder]: string } = {
  title: 'task.modified',
  status: 'task.status'
};

const DashboardDocumentCard: React.FC<DashboardCardProps> = (
  props: DashboardCardProps
) => {
  const limit: number = 10;
  const { user, search: searchProp, onError } = props;
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState(orderMap.title);
  const [orderDirection, setOrderDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [search, setSearch] = useState(searchProp);
  const [files, setFiles] = useState<FileDto[]>([]);
  const {
    data: chart,
    isLoading: isLoadingChart,
    error: chartError
  } = useGetUserPendingDocumentStats(user.id, true);
  const {
    data,
    isLoading: isLoadingFiles,
    error: filesError,
    get
  } = useGetUserPendingDocuments(user.id);
  const isLoading: boolean = isLoadingFiles || isLoadingChart;
  const error: FetchError | undefined = filesError || chartError;

  const items: DashboardItem[] = useMemo(() => {
    return files.map((file: FileDto): DashboardItem => {
      const task = file.task;
      const subTask: Task | undefined = task?.children?.[0] || task;
      const taskKey = task?.parent?.key || task?.key;
      const taskType = task?.parent?.taskType || task?.taskType;
      const primary = (taskKey ? `${taskKey} / ` : '') + file.name;
      const tooltip = primary + (taskType?.label ? ` / ${taskType.label}` : '');
      return {
        id: file.id,
        tooltip,
        to: task && `/portal/documents/file/task/${task?.id}`,
        columns: [
          { tooltip, primary, secondary: formatDate(subTask?.modified, true) },
          { render: () => <TaskStatusLabel task={task} /> }
        ]
      } as DashboardItem;
    });
  }, [files]);

  const sortFiles = useCallback(
    (files: FileDto[]): FileDto[] => {
      return Array.from(files).sort((a: FileDto, b: FileDto) => {
        if (orderDirection === 'DESC') {
          const temp: FileDto = a;
          a = b;
          b = temp;
        }

        switch (order) {
          case 'task.modified': {
            const aSubTask = a.task?.children?.[0] || a.task;
            const bSubTask = b.task?.children?.[0] || b.task;
            if (aSubTask?.modified && bSubTask?.modified) {
              return +aSubTask.modified - +bSubTask.modified;
            }
            break;
          }
          case 'task.status': {
            const aStatus = parseTaskStatus(a.task?.status || '');
            const bStatus = parseTaskStatus(b.task?.status || '');
            return aStatus.localeCompare(bStatus);
          }
        }

        // fallback to name or task key
        const aTaskKey = a.task?.parent?.key || a.task?.key || '';
        const bTaskKey = b.task?.parent?.key || b.task?.key || '';
        const aName = a.name || '';
        const bName = b.name || '';
        return (aTaskKey + aName).localeCompare(bTaskKey + bName);
      });
    },
    [order, orderDirection]
  );

  // reset when searching
  useEffect(() => {
    setFiles([]);
    setPage(0);
    setSearch(searchProp);
  }, [searchProp]);

  useEffect(() => {
    get({ page, limit, search: search || '' });
  }, [get, page, limit, search]);

  useEffect(() => {
    if (data) {
      const newFiles: FileDto[] = data.data
        .filter((newFile: FileDto) => {
          return !files.find((file: FileDto) => file.id === newFile.id);
        })
        .concat(files);
      setFiles(sortFiles(newFiles));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sortFiles]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <BaseDashboardCard
      items={items}
      chart={chart}
      total={chart?.total}
      isLoading={isLoading}
      icon={<FileCopyIcon />}
      title='Pending Document Reviews'
      emptyState='No pending Document reviews.'
      page={page}
      limit={limit}
      orderDirection={orderDirection}
      setPage={setPage}
      setOrderDirection={setOrderDirection}
      isActive={(o: DashboardCardOrder) => orderMap[o] === order}
      setOrder={(order: DashboardCardOrder) => setOrder(orderMap[order])}
    />
  );
};

export default DashboardDocumentCard;
